import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import BankAdd from "./components/Bank/BankAdd";
import BankForm from "./components/Bank/BankForm";
import DashboardPage from "./pages/DashboardPage";
import FinancesRootPage from "./pages/banks/BankRootPage";
import ForgottenPassword from "./pages/authentification/ForgottenPassword";
import ForgottenPasswordReconf from "./pages/authentification/ForgottenPasswordReconf";
import LoginPage from "./pages/authentification/LoginPage";
import NotFound from "./pages/NotFound";
import PropertyShowPage from "./pages/properties/PropertyShowPage";
import SignupPage from "./pages/authentification/SignupPage";
import SignupVerifyPage from "./pages/authentification/SignupVerifyPage";
import TenantsRootPage from "./pages/tenants/RootPage";
import TenantShowPage from "./pages/tenants/ShowPage";
import TenantListPage from "./pages/tenants/ListPage";
import TenantEditPage from "./pages/tenants/EditPage";
import TenantAddGarantPage from "./pages/tenants/AddGarantPage";
import TenantAddRepresentantPage from "./pages/tenants/AddRepresentantPage";
import CandidatProfileAddPage from "./pages/profiles/CandidatProfileAddPage";
import CandidatProfileEditPage from "./pages/profiles/CandidatProfileEditPage";
import CandidatRootPage from "./pages/candidats/CandidatRootPage";
import CandidatShowPage from "./pages/candidats/CandidatShowPage";
import PropertyRootPage from "./pages/properties/PropertyRootPage";
import PropertyAddPage from "./pages/properties/PropertyAddPage";
import PropertyEditPage from "./pages/properties/PropertyEditPage";
import PropertyListPage from "./pages/properties/PropertyListPage";
import LocataireRootPage from "./pages/locataire/LocataireRootPage";
import LocataireShowPage from "./pages/locataire/LocataireShowPage";
import ProfilesBailleurRootPage from "./pages/profiles/ProfilesBailleurRootPage";
import TenantAddPage from "./pages/tenants/TenantAddPage";
import RequireAuth from "./components/Auth/RequireAuth";
import PersistLogin from "./components/Auth/PersistLogin";
import LocataireListCandidaturesPage from "./pages/locataire/LocataireListCandidaturesPage";
import AdminPage from "./pages/AdminPage";
import CheckoutReturn from "./components/Stripe/CheckoutReturn";
import CandidatListPage from "./pages/tenants/CandidatListPage";
import PropertyFolderShowPage from "./pages/propertyFolder/PropertyFolderShowPage";
import MonComptePage from "./pages/MonComptePage";
import OAuthAuthorizePage from "./pages/OAuthAuthorizePage";
import { StrictMode, useEffect } from "react";
import BankSyncForm from "./components/Bank/BankSyncForm";
import BankSyncTransactions from "./components/Bank/BankSyncTransactions";
import useFrontVersion from "./hooks/use-front-version";
import Button from "./components/UI/Button";
import { trackMetaEvent } from "./utils/metaPixel";
import CandidatAddPage from "./pages/tenants/CandidatAddPage";
import { sendPageView, withoutPrivateParams } from "./utils/tracking";
import TBDPage from "./pages/TBDPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import PilotagePage from "./pages/PilotagePage";
import LocataireListPage from "./pages/locataire/LocataireListPage";
import { BankProvider } from "./utils/constants/linxo";
import ProfileBailleurListPage from "./pages/profiles/ProfilesBailleurListPage";
import ProfileBailleurShowPage from "./pages/profiles/ProfileBailleurShowPage";
import PennylaneCallbackPage from "./pages/profiles/PennylaneCallbackPage";
import FiscalitePage from "./pages/FiscalitePage.jsx";
import ComptabilitePage from "./pages/ComptabilitePage";
import CarnetAdressePage from "./pages/CarnetAdressePage.jsx";
import ParrainagePage from "./pages/ParrainagePage.tsx";
import AuthProvider from "./store/AuthProvider.jsx";
import ProfileFilterProvider from "./store/ProfileFilterProvider.tsx";
import ProfileRightsProvider from "./store/ProfileRightsProvider.tsx";
import StripeProvider from "./store/StripeProvider.jsx";
import { NavigationMenuProvider } from "./components/UI/NavigationMenu";
import { ToastProvider } from "./components/UI/Toast/Toast";
import { UploadLockProvider } from "./components/UI/UploadLock";
import { NuqsAdapter } from "nuqs/adapters/react-router/v6";

import useAuth from "./hooks/use-auth.js";

function usePageTracker() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const cleaned = withoutPrivateParams(location);
    if (cleaned) {
      navigate(
        { pathname: location.pathname, search: cleaned.search },
        { replace: true, state: { ...location.state, ...cleaned.state } },
      );
      return;
    }
    sendPageView(location);
    trackMetaEvent("PageView", `pv_${crypto.randomUUID()}`);
  }, [location, navigate]);
}

function useScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
}

function Router() {
  const { auth } = useAuth();
  const { upToDate } = useFrontVersion();
  usePageTracker();
  useScrollToTop();

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route element={<PersistLogin />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/signup/pro" element={<SignupPage pro />} />
        <Route path="/reset-password" element={<ForgottenPassword />} />
        <Route
          path="/init-password/:token"
          element={<ForgottenPasswordReconf />}
        />
        <Route
          path="/init-password-invite/:token"
          element={<ForgottenPasswordReconf isInvite />}
        />
        <Route path="/signup/verify" element={<SignupVerifyPage />} />

        {/* Private routes */}
        <Route element={<PersistLogin />}>
          <Route element={<RequireAuth adminPage />}>
            <Route path="/admin" element={<AdminPage />} />
          </Route>
          <Route element={<RequireAuth />}>
            <Route path="/" element={<Navigate to={auth.home} />} />
            <Route path="/mon-compte" element={<MonComptePage />} />
            <Route
              path="/profiles/pennylane/callback"
              element={<PennylaneCallbackPage />}
            />
            <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />
            <Route path="/tbd" element={<TBDPage />} />
            <Route path="/subscription" element={<SubscriptionPage />} />

            {/* ===================== */}
            {/* Gestion des bailleurs */}
            {/* ===================== */}
            <Route path="/bailleur" element={<DashboardPage />} />
            <Route path="/pro" element={<DashboardPage />} />
            <Route
              path="/bailleur/stripe/return"
              element={<CheckoutReturn />}
            />
            <Route
              path="/bailleur/profiles"
              element={<ProfilesBailleurRootPage />}
            >
              <Route
                path=":profileBailleurId"
                element={<ProfileBailleurShowPage />}
              />
              <Route path="" element={<ProfileBailleurListPage />} />
            </Route>

            <Route path="/bailleur/properties" element={<PropertyRootPage />}>
              <Route
                path="folder/:propertyFolderId"
                element={<PropertyFolderShowPage />}
              >
                <Route path="edit" element={<PropertyEditPage />} />
              </Route>
              <Route path=":propertyId" element={<PropertyShowPage />}>
                <Route path="edit" element={<PropertyEditPage />} />
              </Route>
              <Route path="" element={<PropertyListPage />}>
                <Route path="add" element={<PropertyAddPage />} />
              </Route>
            </Route>

            <Route path="/bailleur/tenants" element={<TenantsRootPage />}>
              <Route path="" element={<TenantListPage />}>
                <Route path={"add"} element={<TenantAddPage />} />
              </Route>
              <Route path=":tenantId" element={<TenantShowPage />}>
                <Route
                  path=":profileId/add_garant"
                  element={<TenantAddGarantPage />}
                />
                <Route
                  path=":profileId/add_representant"
                  element={<TenantAddRepresentantPage />}
                />

                <Route path=":profileId/edit" element={<TenantEditPage />} />
              </Route>
            </Route>
            <Route path="/bailleur/candidats" element={<TenantsRootPage />}>
              <Route path="" element={<CandidatListPage />}>
                <Route path={"add"} element={<CandidatAddPage />} />
              </Route>
              <Route path=":tenantId" element={<TenantShowPage />}>
                <Route
                  path=":profileId/add_garant"
                  element={<TenantAddGarantPage />}
                />
                <Route
                  path=":profileId/add_representant"
                  element={<TenantAddRepresentantPage />}
                />

                <Route path=":profileId/edit" element={<TenantEditPage />} />
              </Route>
            </Route>

            <Route path="bailleur/finances/*" element={<FinancesRootPage />}>
              <Route path="edit/:bankId" element={<BankForm edit={true} />} />
              <Route path="add" element={<BankAdd />} />
              <Route
                path="post-add"
                element={<BankSyncForm provider={BankProvider.NORDIGEN} />}
              />
              <Route path="post-sync" element={<BankSyncTransactions />} />
              <Route
                path="linxo-redirect"
                element={<BankSyncForm provider={BankProvider.LINXO} />}
              />
            </Route>
            <Route path="bailleur/pilotage/*" element={<PilotagePage />} />
            <Route path="bailleur/fiscalite/*" element={<FiscalitePage />} />
            <Route path="bailleur/compta/*" element={<ComptabilitePage />} />
            <Route
              path="bailleur/carnet-adresse/*"
              element={<CarnetAdressePage />}
            />
            <Route path="bailleur/parrainage" element={<ParrainagePage />} />

            {/* ====================== */}
            {/* Gestion des locataires */}
            {/* ====================== */}
            <Route path="/locataire" element={<LocataireRootPage />}>
              <Route path="" element={<LocataireListPage />} />
              <Route
                path="candidatures"
                element={<LocataireListCandidaturesPage />}
              />
              <Route path=":tenantId" element={<LocataireShowPage />} />
            </Route>

            {/* ===================== */}
            {/* Gestion des candidats */}
            {/* ===================== */}
            <Route path="/candidat" element={<CandidatRootPage />}>
              <Route path="" element={<CandidatShowPage />}>
                <Route path="add" element={<CandidatProfileAddPage />} />
                <Route
                  path={":profileId"}
                  element={<CandidatProfileEditPage />}
                />
                <Route
                  path={":profileId/add_garant"}
                  element={<CandidatProfileAddPage isGarant />}
                />
              </Route>
            </Route>
          </Route>
        </Route>

        {/* Catch all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!upToDate && (
        <footer className={"version-footer"}>
          Une nouvelle version de l'application est disponible,{" "}
          <Button variant="tertiary" onClick={() => window.location.reload()}>
            rafraîchissez la page
          </Button>{" "}
          pour une expérience optimale.
        </footer>
      )}
    </>
  );
}

export default function App() {
  return (
    <StrictMode>
      <AuthProvider>
        <ProfileFilterProvider>
          <ProfileRightsProvider>
            <NuqsAdapter>
              <BrowserRouter>
                <NavigationMenuProvider>
                  <StripeProvider>
                    <ToastProvider>
                      <UploadLockProvider>
                        <Router />
                      </UploadLockProvider>
                    </ToastProvider>
                  </StripeProvider>
                </NavigationMenuProvider>
              </BrowserRouter>
            </NuqsAdapter>
          </ProfileRightsProvider>
        </ProfileFilterProvider>
      </AuthProvider>
    </StrictMode>
  );
}
